import type { HttpPort } from "~/lib/http";

export type ProgressStatus = "future" | "checked" | "absent" | "missing" | "pending";

interface RawCheckin {
  checkedIn?: boolean;
  checkedOut?: boolean;
  absent?: boolean;
  goal1?: number | null;
  goal2?: number | null;
  goal3?: number | null;
  mood?: number | null;
}

interface RawWeekStudent {
  name: string;
  monsterName?: string | null;
  monsterColor?: string | null;
  checkins: Record<string, RawCheckin | undefined>;
}

interface RawWeekProgress {
  weekStart: string;
  weekEnd: string;
  weekendConfig?: { saturday?: boolean; sunday?: boolean };
  students: RawWeekStudent[];
}

export interface ProgressDay {
  date: string;
  label: string;
  isToday: boolean;
  isWeekend: boolean;
  enabled: boolean;
}

export interface ProgressCell {
  date: string;
  morning: ProgressStatus;
  evening: ProgressStatus;
}

export interface ProgressStudent {
  name: string;
  monsterName: string | null;
  cells: ProgressCell[];
}

export interface WeekProgress {
  weekStart: string;
  weekEnd: string;
  weekStartLabel: string;
  weekEndLabel: string;
  days: ProgressDay[];
  students: ProgressStudent[];
  totalStudents: number;
  morningToday: number;
  eveningToday: number;
  weekendConfig: { saturday: boolean; sunday: boolean };
}

export type ProgressCommand = { type: "set-weekend"; day: "saturday" | "sunday"; enabled: boolean };

const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function localDate(value: string) {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day, 12);
}

function formatDate(value: string) {
  return localDate(value).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function weekDates(weekStart: string) {
  const start = localDate(weekStart);
  return dayNames.map((_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date.toLocaleDateString("sv-SE");
  });
}

function status(date: string, today: string, checkin: RawCheckin | undefined, phase: "morning" | "evening"): ProgressStatus {
  if (date > today) return "future";
  if (phase === "morning" ? checkin?.checkedIn : checkin?.checkedOut) return "checked";
  if (checkin?.absent) return "absent";
  return date < today ? "missing" : "pending";
}

export function createProgressModule(http: HttpPort, token: string, today = () => new Date().toLocaleDateString("sv-SE")) {
  return {
    async load(classId: string): Promise<WeekProgress> {
      const raw = await http.request<RawWeekProgress>({ path: `/checkins/${classId}/week`, token });
      const currentDate = today();
      const dates = weekDates(raw.weekStart);
      const weekendConfig = {
        saturday: raw.weekendConfig?.saturday ?? false,
        sunday: raw.weekendConfig?.sunday ?? false
      };
      const students = raw.students.map((student) => ({
        name: student.name,
        monsterName: student.monsterName ?? null,
        cells: dates.map((date) => ({
          date,
          morning: status(date, currentDate, student.checkins[date], "morning"),
          evening: status(date, currentDate, student.checkins[date], "evening")
        }))
      }));
      return {
        weekStart: raw.weekStart,
        weekEnd: raw.weekEnd,
        weekStartLabel: formatDate(raw.weekStart),
        weekEndLabel: formatDate(raw.weekEnd),
        days: dates.map((date, index) => ({
          date,
          label: dayNames[index],
          isToday: date === currentDate,
          isWeekend: index >= 5,
          enabled: index < 5 || (index === 5 ? weekendConfig.saturday : weekendConfig.sunday)
        })),
        students,
        totalStudents: students.length,
        morningToday: raw.students.filter((student) => student.checkins[currentDate]?.checkedIn).length,
        eveningToday: raw.students.filter((student) => student.checkins[currentDate]?.checkedOut).length,
        weekendConfig
      };
    },

    async execute(classId: string, command: ProgressCommand): Promise<void> {
      await http.request({
        path: `/classes/${classId}/weekend-checkin`,
        method: "PATCH",
        token,
        body: {
          saturday: command.day === "saturday" ? command.enabled : undefined,
          sunday: command.day === "sunday" ? command.enabled : undefined
        }
      });
    }
  };
}

export type ProgressModule = ReturnType<typeof createProgressModule>;
